import {Component, OnInit} from '@angular/core';
import {User} from '../../../core/entities/user/user.entity';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import { CurrencyPipe } from '@angular/common';
import {CreatorDashboardService} from '../../services/creator/creator-dashboard.service';
import {DashboardStatistics} from '../../entities/dashboard/dashboard-statistics.entity';
import {OnboardingBannerComponent} from '../../components/onboarding/onboarding-banner/onboarding-banner.component';
import {ServiceService} from '../../services/service/service.service';
import {Router} from '@angular/router';
import {ShareProfileModal} from '../../components/share-profile-modal/share-profile-modal';
import {ViewStatsComponent} from '../../components/view-stats/view-stats.component';
import {PhotographerViewStatistics} from '../../entities/dashboard/view-stats.entity';
import {DashboardOnboarding} from '../../entities/dashboard/onboarding.entity';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ShareInstagramModal} from '../../components/share-instagram-modal/share-instagram-modal';
import {environment} from '../../../../environments/environment';

@Component({
  selector: 'app-dashboard',
  imports: [
    CurrencyPipe,
    OnboardingBannerComponent,
    ShareProfileModal,
    ShareInstagramModal,
    ViewStatsComponent,
    TranslocoPipe
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  public user: User|null = null;
  public statistics: DashboardStatistics|null = null;
  public viewStats: PhotographerViewStatistics|null = null;
  public viewStatsLoading = false;
  public viewStatsError: string|null = null;
  public onboarding: DashboardOnboarding|null = null;
  public isShareProfileModalShown = false;
  public isShareInstagramModalShown = false;
  public shareProfileUrl = '';

  public constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly creatorDashboardService: CreatorDashboardService,
    private readonly serviceService: ServiceService,
    private readonly router: Router,
    private readonly transloco: TranslocoService,
  ) {
  }

  public ngOnInit(): void {
    this.user = this.authenticationService.getCurrentUser();
    this.shareProfileUrl = this.buildShareProfileUrl();

    // First load services
    this.serviceService.getServices().subscribe((services) => {
      const needsOnboarding = this.isOnboarding(services.length);

      if (needsOnboarding) {
        this.router.navigate(['/manage/onboarding']);
        return;
      }

      // Only fetch stats if not in onboarding
      this.creatorDashboardService.getStatistics().subscribe((statistics) => {
        this.statistics = statistics;
      });

      this.loadViewStatistics();
      this.loadOnboarding();
    });
  }

  private isOnboarding(numberOfServices: number): boolean {
    if (!this.user) {
      return false;
    }

    return this.user.firstname === null || this.user.firstname === ''
      || this.user.image === null
      || this.user.biography === null || this.user.biography === ''
      || (this.user.tags?.length ?? 0) === 0
      || numberOfServices === 0;
  }

  private loadViewStatistics(): void {
    this.viewStatsLoading = true;
    this.viewStatsError = null;

    this.creatorDashboardService.getViewStatistics().subscribe({
      next: (stats) => {
        this.viewStats = stats;
        this.viewStatsLoading = false;
      },
      error: () => {
        this.viewStatsLoading = false;
        this.viewStatsError = this.transloco.translate('creator.dashboard.views.error');
      }
    });
  }

  public openShareProfileModal(): void {
    this.isShareProfileModalShown = true;
  }

  public closeShareProfileModal(): void {
    this.isShareProfileModalShown = false;
  }

  public openShareInstagramModal(): void {
    this.shareProfileUrl = this.buildShareProfileUrl();
    this.isShareInstagramModalShown = true;
  }

  public closeShareInstagramModal(): void {
    this.isShareInstagramModalShown = false;
  }

  private loadOnboarding(): void {
    this.creatorDashboardService.getOnboarding().subscribe((onboarding) => {
      this.onboarding = onboarding;
    });
  }

  private buildShareProfileUrl(): string {
    if (!this.user) {
      return '';
    }

    const baseUrl = environment.frontBaseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '');
    if (!baseUrl) {
      return '';
    }

    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const shareUrl = new URL(`photographer/${this.user.slug}`, normalizedBase);
    shareUrl.searchParams.set('utm_source', 'creator');
    shareUrl.searchParams.set('utm_medium', 'share-instagram-modal');
    return shareUrl.toString();
  }
}
